import Big from 'big.js';
import dayjs from 'dayjs';
import { MarginCalculatorModel } from '../../models';
import { HousingState, HousingTypeState } from './housing.state';

export const enum HousingTypes {
    Apt = 1,
    Hotel = 2,
    FacilityProvided = 3
}

export class HousingStateHelpers {
    static getHousingType(housingTypeId: number): HousingTypeState {
        switch (housingTypeId) {
            case HousingTypes.Apt:
                return { id: housingTypeId, name: 'Apt' };
            case HousingTypes.Hotel:
                return { id: housingTypeId, name: 'Hotel' };
            case HousingTypes.FacilityProvided:
                return { id: housingTypeId, name: 'Facility Provided' };
            default:
                return { id: housingTypeId, name: 'Unknown' };
        }
    }

    /**
     * Returns the monthly rent from the margin calculator model
     *
     * @param margin margin calculator model
     * @returns
     */
    static getMonthlyRent(margin: MarginCalculatorModel): number {
        if (margin?.detail?.isAyaHousing === true) {
            if (margin.expenseAll.housingType === HousingTypes.Hotel) {
                return margin.detail.extendedStayHotel;
            }
            return margin.detail.monthlyHousing;
        }
        return 0;
    }

    /**
     * Returns the housing move in date for the contract
     *
     * @param startDate contract start date
     * @returns
     */
    static getMoveInDate(startDate: string | Date): Date {
        return dayjs(new Date(startDate)).subtract(2, 'day').toDate();
    }

    /**
     * Returns the housing move out date for the contract
     *
     * @param endDate contract end date
     * @returns
     */
    static getMoveOutDate(endDate: string | Date): Date {
        return dayjs(new Date(endDate)).add(2, 'day').toDate();
    }

    /**
     * Returns the difference in days between the start and end date
     *
     * @param startDate
     * @param endDate
     */
    static getDays(startDate: Date, endDate: Date): number {
        return dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'days');
    }

    /**
     * Returns the prorate multiplier for housing
     *
     * @param housingDays number of days for housing
     * @param contractDays number of days for contract
     * @returns
     */
    static getProrateMultiplier(housingDays: number, contractDays: number): number {
        if (isNaN(housingDays) || isNaN(contractDays)) {
            return 1;
        }
        const prorate = new Big(housingDays).minus(contractDays).minus(4);
        return new Big(1).add(prorate.div(contractDays)).toNumber();
    }

    /**
     * Returns the cost per contract
     *
     * @param monthlyCost monthly cost
     * @param weeks weeks for contract
     * @param prorateMultiplier prorate multiplier for housing
     * @returns
     */
    static getProratedPerContractCost(monthlyCost: number, weeks: number, prorateMultiplier: number): number {
        if (isNaN(monthlyCost) || isNaN(weeks) || isNaN(prorateMultiplier)) {
            return 0;
        }
        return new Big(monthlyCost).times(new Big(12).div(52)).times(weeks).times(prorateMultiplier).toNumber();
    }

    /**
     * Returns the calculated move out expenses for housing
     *
     * @param bedrooms number of bedrooms
     * @returns
     */
    static getMoveOutExpenses(bedrooms: number): number {
        switch (bedrooms) {
            case 1:
                return 200;
            case 2:
                return 300;
            default:
                return 0;
        }
    }

    /**
     * Returns the calculated furniture delivery expenses for housing
     *
     * @param bedrooms number of bedrooms
     * @returns
     */
    static getFurnitureDelivery(bedrooms: number): number {
        switch (bedrooms) {
            case 1:
                return 50;
            case 2:
                return 150;
            default:
                return 0;
        }
    }

    /**
     * Returns the calculated furniture expenses for housing
     *
     * @param bedrooms number of bedrooms
     * @returns
     */
    static getFurniture(bedrooms: number): number {
        switch (bedrooms) {
            case 1:
                return 425;
            case 2:
                return 500;
            default:
                return 0;
        }
    }

    /**
     * Returns the calculated utilities expenses for housing
     *
     * @param bedrooms number of bedrooms
     * @returns
     */
    static getUtilities(bedrooms: number): number {
        switch (bedrooms) {
            case 1:
                return 100;
            case 2:
                return 150;
            default:
                return 0;
        }
    }

    /**
     * Returns the current housing state merged with the updated housing type options based on the
     * new housing type.
     * 
     * TCV-3385 Unselect any previously selected housewares when changing housing type.
     * TCV-3537 Move in and out dates apply only to apartment or hotel and will otherwise be bull.
     *          The two date fields need to be populated when changing housing type from
     *          no housing or facility provided to apartment or hotel.
     * 
     * @param current the current housing state
     * @param housingTypeId the new housing type id
     * @param startDate the contract start date to be used to calculate move in date
     * @param endDate the contract end date to be used to calculate move out date
     * @returns 
     */
    static getHousingTypeOptions(current: HousingState, housingTypeId: number, startDate: string, endDate: string): HousingState {
        let moveInDate: string = null, moveOutDate: string = null;

        if (housingTypeId === HousingTypes.Apt || housingTypeId === HousingTypes.Hotel) {
            moveInDate = startDate ? HousingStateHelpers.getMoveInDate(startDate).toLocaleDateString() : null;
            moveOutDate = endDate ? HousingStateHelpers.getMoveOutDate(endDate).toLocaleDateString() : null;
        }

        return {
            ...current,
            housingType: HousingStateHelpers.getHousingType(housingTypeId),
            bedrooms: 1,
            moveOutExpenses: HousingStateHelpers.getMoveOutExpenses(1),
            furnitureDelivery: HousingStateHelpers.getFurnitureDelivery(1),
            furniture: HousingStateHelpers.getFurniture(1),
            utilities: HousingStateHelpers.getUtilities(1),
            monthlyHousing: 0,
            extendedStayHotel: 0,
            moveInDate,
            moveOutDate,
            housewares: []
        };
    }
}
